import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Checkbox } from './ui/checkbox';
import { 
  Lightbulb, 
  Check, 
  X, 
  Sparkles,
  Info
} from 'lucide-react';
import type { SuggestedConfigItem } from '../types';

interface ChangeOrderSuggestionsProps {
  suggestions: SuggestedConfigItem[];
  onAcceptSuggestion: (suggestionId: string) => void;
  onRejectSuggestion: (suggestionId: string) => void;
  onAcceptAll: () => void;
  readOnly?: boolean;
}

export function ChangeOrderSuggestions({ 
  suggestions,
  onAcceptSuggestion,
  onRejectSuggestion,
  onAcceptAll,
  readOnly = false
}: ChangeOrderSuggestionsProps) {
  if (suggestions.length === 0) {
    return null;
  }

  const pendingSuggestions = suggestions.filter(s => !s.accepted);
  const acceptedSuggestions = suggestions.filter(s => s.accepted);

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'sla': 'bg-blue-500',
      'queue': 'bg-purple-500',
      'approval': 'bg-green-500',
      'asset': 'bg-orange-500',
      'automation': 'bg-red-500',
      'notification': 'bg-yellow-500',
      'email': 'bg-pink-500',
      'portal': 'bg-indigo-500',
      'request-type-group': 'bg-cyan-500',
      'knowledge-base': 'bg-teal-500',
      'integration': 'bg-violet-500',
      'csat': 'bg-rose-500',
      'dashboard': 'bg-emerald-500',
      'field-configuration': 'bg-amber-500',
      'service-catalog': 'bg-lime-500',
      'team-settings': 'bg-fuchsia-500',
    };
    return colors[category] || 'bg-gray-500';
  };

  return (
    <Card className="border-primary">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              AI-Suggested Configuration Items
            </CardTitle>
            <CardDescription>
              Based on the change order scope, these configuration items may be needed
            </CardDescription>
          </div>
          {pendingSuggestions.length > 0 && !readOnly && (
            <Button
              variant="outline"
              size="sm"
              onClick={onAcceptAll}
            >
              <Check className="mr-2 h-4 w-4" />
              Accept All ({pendingSuggestions.length})
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {pendingSuggestions.length > 0 && (
          <>
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Review these suggestions and accept the ones that apply to this SOW. Accepted items will be added to the configuration scope.
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              {pendingSuggestions.map(suggestion => (
                <Card key={suggestion.id} className="bg-muted/50">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="flex items-center gap-2">
                          <Lightbulb className="h-4 w-4 text-yellow-500" />
                          {suggestion.name}
                          <Badge className={getCategoryColor(suggestion.category)}>
                            {suggestion.category}
                          </Badge>
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {suggestion.description}
                        </CardDescription>
                      </div>
                      {!readOnly && (
                        <div className="flex gap-2 ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onAcceptSuggestion(suggestion.id)}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onRejectSuggestion(suggestion.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-background rounded p-3 border">
                      <p className="text-muted-foreground">
                        <strong>Why suggested:</strong> {suggestion.reasoning}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}

        {acceptedSuggestions.length > 0 && (
          <div>
            <h4 className="mb-3 flex items-center gap-2 text-green-600">
              <Check className="h-4 w-4" />
              Accepted Suggestions ({acceptedSuggestions.length})
            </h4>
            <div className="space-y-2">
              {acceptedSuggestions.map(suggestion => (
                <div 
                  key={suggestion.id} 
                  className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded"
                >
                  <div className="flex items-center gap-3">
                    <Check className="h-4 w-4 text-green-600" />
                    <div>
                      <p>{suggestion.name}</p>
                      <p className="text-muted-foreground">{suggestion.description}</p>
                    </div>
                  </div>
                  <Badge className={getCategoryColor(suggestion.category)}>
                    {suggestion.category}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {pendingSuggestions.length === 0 && acceptedSuggestions.length === 0 && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              No configuration items were suggested for this change order. You can manually add configuration items as needed.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
